import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert } from 'react-bootstrap';
import axios from '../utils/api';
import '../styles/Settings.css';
import '../styles/ActivityLogs.css';

const ACTION_LABELS = {
  login: 'Sign in',
  logout: 'Sign out',
  register: 'Register',
  password_reset_request: 'Password reset request',
  password_reset: 'Password reset',
  password_change: 'Password change',
  staff_create: 'Create staff',
  staff_update: 'Update staff',
  staff_delete: 'Remove staff',
  broadcast: 'Broadcast email',
  schedule: 'Schedule email',
  follow_up: 'Follow-up email',
  email_settings: 'Email settings',
  prompt_settings: 'Prompt settings',
  platform_settings: 'Platform settings',
  ticket_create: 'Open ticket',
  ticket_reply: 'Ticket reply',
  role_update: 'Change role',
  user_delete: 'Delete user',
  other: 'Other',
};

const formatWhen = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
};

const ActivityLogs = () => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const [logs, setLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [actions, setActions] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [filters, setFilters] = useState({ action: '', user_id: '', q: '', date_from: '', date_to: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const canAccess = user?.role === 'master_admin';

  const loadLogs = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { page, page_size: pageSize };
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params[key] = value;
      });
      const res = await axios.get('/auth/activity-logs/', { params });
      setLogs(res.data?.results || []);
      setCount(res.data?.count || 0);
      setUsers(res.data?.users || []);
      setActions(res.data?.actions || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load activity logs.');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filters]);

  useEffect(() => {
    document.title = 'Activity Logs';
    if (!canAccess) {
      navigate('/dashboard', { replace: true });
      return;
    }
    loadLogs();
  }, [canAccess, navigate, loadLogs]);

  if (!canAccess) return null;

  const totalPages = Math.max(1, Math.ceil(count / pageSize));

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setPage(1);
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <div className="settings-page">
      <div className="page-header">
        <h1>Activity Logs</h1>
        <p className="page-description">
          What each admin and staff member did on this instance — sign-in, broadcasts, staff, and settings.
          File analysis is tracked under AI Usage, not here.
        </p>
      </div>

      {error && (
        <Alert variant="danger" className="settings-alert">{error}</Alert>
      )}

      <div className="settings-section">
        <div className="section-header">
          <i className="fas fa-filter"></i>
          <h2>Filters</h2>
        </div>
        <div className="activity-grid">
          <div className="form-group">
            <label htmlFor="action">Action</label>
            <select id="action" name="action" className="form-control" value={filters.action} onChange={handleFilterChange}>
              <option value="">All actions</option>
              {actions.map((action) => (
                <option key={action} value={action}>{ACTION_LABELS[action] || action}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="user_id">Person</label>
            <select id="user_id" name="user_id" className="form-control" value={filters.user_id} onChange={handleFilterChange}>
              <option value="">Everyone</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.full_name || u.email} ({u.role === 'master_admin' ? 'Admin' : 'Staff'})
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="date_from">From</label>
            <input id="date_from" name="date_from" type="date" className="form-control" value={filters.date_from} onChange={handleFilterChange} />
          </div>
          <div className="form-group">
            <label htmlFor="date_to">To</label>
            <input id="date_to" name="date_to" type="date" className="form-control" value={filters.date_to} onChange={handleFilterChange} />
          </div>
          <div className="form-group" style={{ gridColumn: '1 / -1' }}>
            <label htmlFor="q">Search</label>
            <input
              id="q"
              name="q"
              type="search"
              className="form-control"
              placeholder="Name, email, or description"
              value={filters.q}
              onChange={handleFilterChange}
            />
          </div>
        </div>
      </div>

      <div className="settings-section">
        <div className="section-header" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <i className="fas fa-clipboard-list"></i>
            <h2>{count} event{count === 1 ? '' : 's'}</h2>
          </div>
        </div>

        {loading ? (
          <p>Loading activity…</p>
        ) : !logs.length ? (
          <p style={{ color: '#64748b' }}>No activity yet. Actions after this update will appear here.</p>
        ) : (
          <div className="activity-table-wrap">
            <table className="activity-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Who</th>
                  <th>Action</th>
                  <th>What happened</th>
                  <th>IP</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr key={log.id}>
                    <td>{formatWhen(log.created_at)}</td>
                    <td>
                      <strong>{log.full_name || 'Unknown'}</strong>
                      <div className="activity-sub">
                        {log.user_email}
                        {log.role ? ` · ${log.role === 'master_admin' ? 'Admin' : 'Staff'}` : ''}
                      </div>
                    </td>
                    <td>
                      <span className="activity-pill">{ACTION_LABELS[log.action] || log.action}</span>
                    </td>
                    <td>{log.description}</td>
                    <td className="activity-sub">{log.ip_address || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16, gap: 12, flexWrap: 'wrap' }}>
            <button
              type="button"
              className="btn btn-outline-primary"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </button>
            <span>Page {page} of {totalPages}</span>
            <button
              type="button"
              className="btn btn-outline-primary"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityLogs;
